import { of } from 'rxjs'
import { List } from 'immutable'
import { complement } from 'ramda'

import { HistoryRecord } from './audit-history'
import { updateConsent } from './consents'
import { collateTPPs } from './tpps'

const GROUPING_NAMES = [
  'ACTIVE',
  'EXPIRED',
  'DECLINED',
  'WITHDRAWN',
  'DELETED',
  'TPP_SUSPENDED',
  'TPP_CLIENT_SUSPENDED',
  'TPP_REVOKED',
  'BLOCKED',
]

const CONSENT_STATUS_TO_HISTORY_LABEL = {
  ACCEPTED: 'Created',
  AUTHORIZED: 'Connected since',
  DECLINED: 'Declined',
  DELETED: 'Deleted',
  BLOCKED: 'Request blocked by bank',
  WITHDRAWN: 'Cancelled',
  // this is whatever MMonks gives us, and nothing else.
}

const GROUPINGS = GROUPING_NAMES.reduce(
  (groupings, grouping) =>
    Object.assign({}, groupings, { [grouping]: grouping }),
  {}
)

const STATE_NAMES = [
  ...GROUPING_NAMES,
  'AUTHORIZED',
  'ACCEPTED',
  'FAILED',
  'REVOKED',
  'SUSPENDED',
]

const STATES = STATE_NAMES.reduce(
  (states, state) => Object.assign({}, states, { [state]: state }),
  {}
)

export const isActive = access =>
  access.get('accessGrouping') === GROUPINGS.ACTIVE

const isActiveTpp = tpp => tpp.get('consents').some(isActive)

const updateAccessesGeneratedState = state => {
  const accesses = state
    .getIn(['accesses', 'index'])
    .valueSeq()
    .sort((a, b) => a.createdAt - b.createdAt)
  const avatarColours = state.getIn(['brand', 'avatarColours'], List())
  const tpps = collateTPPs(accesses, avatarColours)
  const activeTpps = tpps.filter(isActiveTpp)
  const inactiveTpps = tpps.filter(complement(isActiveTpp))

  return state
    .setIn(['accesses', 'tpps'], tpps)
    .setIn(['accesses', 'activeTpps'], activeTpps)
    .setIn(['accesses', 'inactiveTpps'], inactiveTpps)
}

const updateAccessesGeneratedState$ = of(updateAccessesGeneratedState)

function updateAccessHistoryState(accessId, consentAudits) {
  return state =>
    state
      .updateIn(['accesses', 'index', accessId], access =>
        access.set('history', getHistory(access, consentAudits))
      )
      .update(updateAccessesGeneratedState)
}

function toHistoryRecords(consentAudit) {
  return new HistoryRecord({
    date: consentAudit.changeAt,
    status: consentAudit.status,
    label: CONSENT_STATUS_TO_HISTORY_LABEL[consentAudit.status] || '-',
  })
}

export function getHistoryForCurrentUser(access, isStaffMember = false) {
  return isStaffMember
    ? access.history
    : access.history.filter(({ status }) => status !== 'ACCEPTED')
}

function getHistory(access, consentAudits) {
  const history = consentAudits.map(toHistoryRecords)

  switch (access.get('accessGrouping')) {
    case STATES.TPP_REVOKED:
    case STATES.REVOKED:
      return history
        .push(
          new HistoryRecord({
            date: null,
            label: 'Access revoked',
          })
        )
        .reverse()
    case STATES.SUSPENDED:
    case STATES.TPP_CLIENT_SUSPENDED:
    case STATES.TPP_SUSPENDED:
      return history
        .push(
          new HistoryRecord({
            date: null,
            label: 'Access suspended',
          })
        )
        .reverse()
    case STATES.EXPIRED:
      return history
        .push(
          new HistoryRecord({
            date: access.get('accessExpiresAt'),
            label: 'Access expired',
          })
        )
        .reverse()
    default:
      return history.reverse()
  }
}

const updateAccessStateAfterLoad = (updatedAccess, consentAudits) => state =>
  state
    .updateIn(['accesses', 'index', updatedAccess.get('id')], access =>
      updateConsent(access, updatedAccess).update(access =>
        access.get('history').size > 0
          ? access.set('history', getHistory(access, consentAudits))
          : access
      )
    )
    .update(updateAccessesGeneratedState)

function accessDescriptiveDate(access) {
  const { modifiedAt, accessExpiresAt, accessGrouping, createdAt } =
    access.toJS()

  switch (accessGrouping) {
    case GROUPINGS.DECLINED:
      return { label: 'Declined', date: modifiedAt, createdAt }
    case GROUPINGS.DELETED:
      return { label: 'Deleted', date: modifiedAt, createdAt }
    case GROUPINGS.WITHDRAWN:
      return { label: 'Cancelled', date: modifiedAt, createdAt }
    case GROUPINGS.EXPIRED:
      return { label: 'Expired', date: accessExpiresAt, createdAt }
    case GROUPINGS.BLOCKED:
      return { label: 'Blocked', date: modifiedAt, createdAt }
    case GROUPINGS.TPP_REVOKED:
      return { label: 'Revoked', date: '', createdAt }
    case GROUPINGS.TPP_SUSPENDED:
    case GROUPINGS.TPP_CLIENT_SUSPENDED:
      return { label: 'Suspended', date: '', createdAt }
    default:
      return {
        // Handle non-expiring consent
        label: accessExpiresAt === null ? 'Ongoing' : 'Expires',
        date: accessExpiresAt,
        createdAt,
      }
  }
}

const processAccessConsents = consents => consents

export {
  GROUPINGS,
  STATES,
  processAccessConsents,
  updateAccessesGeneratedState$,
  updateAccessStateAfterLoad,
  updateAccessHistoryState,
  accessDescriptiveDate,
}
